import { api } from "../api";
import {
  openModal, closeModal, modalBody, setModalTitle, setCloseGuard,
  openModal2, closeModal2, modalBody2,
} from "../components/modal";
import { confirmDialog, infoDialog } from "../components/confirm-dialog";
import { openCsvImportModal } from "../components/csv-import-modal";
import { showToast } from "../utils/toast";
import type { School, PlatformStats, SchoolUser, AdminSchoolDepartment } from "../types";
import { Icons } from "../utils/icons";
import { validatePassword, mountPasswordChecklist } from "../utils/validation";
import { esc } from "../utils/html";

let schools: School[] = [];
/** Seitencontainer. Aus dem geöffneten Fenster heraus müssen Tabelle und Kennzahlen
 *  weiter erreichbar sein, damit der Hintergrund zum Fenster passt. */
let page: HTMLElement;

async function load() {
  schools = await api.admin.schools.list();
}

/**
 * Vergleicht zwei IDs unabhaengig vom Typ.
 *
 * Alle Tabellen nutzen BIGSERIAL. Der pg-Treiber liefert BIGINT als Zeichenkette,
 * weil es nicht sicher in eine JS-Zahl passt, und das Backend reicht den Wert
 * unveraendert durch. School.id ist zur Laufzeit also eine Zeichenkette, obwohl
 * der Typ eine Zahl verspricht. Ein Vergleich mit dem Zahlenwert aus einem
 * data-Attribut schlaegt deshalb fehl.
 */
function sameId(a: string | number | null | undefined, b: string | number | null | undefined): boolean {
  return a != null && b != null && String(a) === String(b);
}

// ── Module ──

/**
 * Schüler-Portal, Planung und Stundenplan verhalten sich identisch: dauerhaft
 * freischalten oder entziehen, dazu eine Testphase mit Enddatum. Sie stehen
 * deshalb als Konfiguration hier und nicht dreimal als kopierter Block im
 * Formular. Ein viertes Modul ist ein weiterer Eintrag in dieser Liste.
 *
 * Ob ein Modul am Ende aktiv ist, entscheidet der Server aus Freischaltung,
 * Testphase und Abo (services/feature-toggle.ts). Das Formular zeigt deshalb
 * immer den Stand vom Server und rechnet nichts selbst aus.
 */
type ModuleKey = "schuelerPortal" | "planung" | "stundenplan";

interface ModuleConfig {
  key: ModuleKey;
  label: string;
  /** Badge-Klasse für den aktiven Zustand. */
  badgeClass: string;
  enabled: (s: School) => boolean;
  manualGrant: (s: School) => boolean;
  trialEndsAt: (s: School) => string | null | undefined;
  toggle: (id: number, enabled: boolean) => Promise<unknown>;
  setTrial: (id: number, trialEndsAt: string | null) => Promise<unknown>;
  /** Was das dauerhafte Freischalten technisch bewirkt. */
  grantEffect: (s: School) => string;
  /** Was der Entzug technisch bewirkt. */
  revokeEffect: (s: School) => string;
  /** Erklärung, solange das Modul weder freigeschaltet noch aktiv ist. */
  offHint: (s: School) => string;
}

const GRANTED_HINT = "Dauerhaft freigeschaltet: Das Modul bleibt aktiv, unabhängig von Testphase und Abo. Beim Entziehen entscheiden wieder Testphase und Abo.";
const ENTITLED_HINT = "Aktiv über Testphase oder Abo. Läuft beides aus, wird das Modul automatisch abgeschaltet. Dauerhaft freischalten macht es davon unabhängig.";

const MODULES: ModuleConfig[] = [
  {
    key: "schuelerPortal",
    label: "Schüler-Portal",
    badgeClass: "badge-purple",
    enabled: s => !!s.schuelerPortalEnabled,
    manualGrant: s => !!s.schuelerPortalManualGrant,
    trialEndsAt: s => s.schuelerPortalTrialEndsAt,
    toggle: (id, enabled) => api.admin.schools.toggleSchuelerPortal(id, enabled),
    setTrial: (id, trialEndsAt) => api.admin.schools.updateSchuelerPortalTrial(id, trialEndsAt),
    grantEffect: s => `Das Modul bleibt aktiv, unabhängig von Testphase und Abo. Die Keycloak-Gruppe /schuelerPortal/${s.slug} wird angelegt.`,
    revokeEffect: () => "Danach entscheiden wieder Testphase und Abo. Ohne beides werden alle Schüler-Logins pausiert; die Zuordnung bleibt erhalten und lebt beim Wiedereinschalten automatisch auf.",
    offHint: s => `Schüler können keinen eigenen Login erhalten. Freischalten legt die Keycloak-Gruppe /schuelerPortal/${s.slug} an.`,
  },
  {
    key: "planung",
    label: "Planung",
    badgeClass: "badge-green",
    enabled: s => !!s.planungEnabled,
    manualGrant: s => !!s.planungManualGrant,
    trialEndsAt: s => s.planungTrialEndsAt,
    toggle: (id, enabled) => api.admin.schools.togglePlanung(id, enabled),
    setTrial: (id, trialEndsAt) => api.admin.schools.updatePlanungTrial(id, trialEndsAt),
    grantEffect: s => `Das Modul bleibt aktiv, unabhängig von Testphase und Abo. Die Rolle 'planung' wird der Keycloak-Gruppe /schulen/${s.slug} zugewiesen.`,
    revokeEffect: s => `Danach entscheiden wieder Testphase und Abo. Ohne beides wird die Rolle 'planung' von der Keycloak-Gruppe /schulen/${s.slug} entfernt.`,
    offHint: s => `Planung ist inaktiv. Freischalten weist der Keycloak-Gruppe /schulen/${s.slug} die Rolle 'planung' zu.`,
  },
  {
    key: "stundenplan",
    label: "Stundenplan",
    badgeClass: "badge-blue",
    enabled: s => !!s.stundenplanEnabled,
    manualGrant: s => !!s.stundenplanManualGrant,
    trialEndsAt: s => s.stundenplanTrialEndsAt,
    toggle: (id, enabled) => api.admin.schools.toggleStundenplan(id, enabled),
    setTrial: (id, trialEndsAt) => api.admin.schools.updateStundenplanTrial(id, trialEndsAt),
    grantEffect: s => `Das Modul bleibt aktiv, unabhängig von Testphase und Abo. Die Rolle 'stundenplan' wird der Keycloak-Gruppe /schulen/${s.slug} zugewiesen.`,
    revokeEffect: s => `Danach entscheiden wieder Testphase und Abo. Ohne beides wird die Rolle 'stundenplan' von der Keycloak-Gruppe /schulen/${s.slug} entfernt.`,
    offHint: s => `Stundenplan ist inaktiv. Freischalten weist der Keycloak-Gruppe /schulen/${s.slug} die Rolle 'stundenplan' zu.`,
  },
];

// ── Testphasen ──

const DAY = 86400000;
/** Ab hier gilt eine Testphase als bald auslaufend. */
const TRIAL_SOON_DAYS = 14;
/** So lange bleibt eine abgelaufene Testphase in der Hinweisleiste stehen. */
const TRIAL_EXPIRED_DAYS = 30;

interface TrialInfo { endsAt: Date; daysLeft: number; expired: boolean; }

function trialInfo(iso: string | null | undefined): TrialInfo | null {
  if (!iso) return null;
  const endsAt = new Date(iso);
  if (isNaN(endsAt.getTime())) return null;
  return {
    endsAt,
    daysLeft: Math.ceil((endsAt.getTime() - Date.now()) / DAY),
    expired: endsAt.getTime() <= Date.now(),
  };
}

function formatDate(d: Date): string { return d.toLocaleDateString("de-DE"); }
function dayLabel(n: number): string { return `${n} Tag${n === 1 ? "" : "e"}`; }
function dateInputValue(d: Date): string { return d.toISOString().split("T")[0]; }

// ── Kennzahlen und Tabelle ──

function renderStats(stats: PlatformStats) {
  page.querySelector("#admin-stats")!.innerHTML = [
    { label: "Schulen (gesamt)", value: stats.totalSchools, icon: Icons.buildingOffice, bg: "var(--primary)" },
    { label: "Schulen (aktiv)", value: stats.activeSchools, icon: Icons.checkCircle, bg: "var(--green-500)" },
    { label: "Schüler (gesamt)", value: stats.totalStudents, icon: Icons.user, bg: "#a855f7" },
    { label: "Praktikumsstellen", value: stats.totalPractices, icon: Icons.buildingOffice2, bg: "var(--amber-500)" },
    { label: "Zuweisungen", value: stats.totalAssignments, icon: Icons.clipboardDocumentList, bg: "var(--gray-600)" },
  ].map(s => `<div class="card stat-card"><div class="stat-icon" style="background:${s.bg};color:#fff">${s.icon}</div><div class="stat-label">${s.label}</div><div class="stat-value">${s.value}</div></div>`).join("");
}

async function refreshStats() {
  renderStats(await api.admin.stats());
}

/**
 * Eine Modul-Spalte der Schulliste.
 *
 * Der Haken steht für unbefristet: dauerhaft freigeschaltet oder über ein Abo.
 * Das T steht für die befristete Testphase. Eine leere Zelle heißt, dass das
 * Modul nicht läuft, auch wenn eine Testphase dafür abgelaufen ist. Was der
 * Zustand im Einzelnen bedeutet, sagt der Tooltip; die Restlaufzeiten stehen in
 * der Hinweisleiste über der Tabelle.
 */
function moduleCell(s: School, m: ModuleConfig): string {
  const trial = trialInfo(m.trialEndsAt(s));
  const cell = (mark: string, cls: string, title: string) =>
    `<td class="admin-mod ${cls}" title="${esc(`${m.label}: ${title}`)}">${mark}</td>`;

  if (m.manualGrant(s)) return cell("✓", "admin-mod-on", "dauerhaft freigeschaltet");
  if (m.enabled(s)) {
    return trial && !trial.expired
      ? cell("T", "admin-mod-trial", `Testphase, endet am ${formatDate(trial.endsAt)} (noch ${dayLabel(trial.daysLeft)})`)
      : cell("✓", "admin-mod-on", "über Abo aktiv");
  }
  return cell("", "", trial?.expired ? `Testphase abgelaufen am ${formatDate(trial.endsAt)}` : "nicht aktiv");
}

/** DRK ist ein reines Datenbank-Flag, es kennt weder Testphase noch Abo. */
function drkCell(s: School): string {
  return s.drkKreisverbandEnabled
    ? `<td class="admin-mod admin-mod-on" title="DRK Kreisverbände: freigeschaltet">✓</td>`
    : `<td class="admin-mod" title="DRK Kreisverbände: nicht freigeschaltet"></td>`;
}

function renderTable() {
  const tbody = page.querySelector("#school-tbody")!;
  if (schools.length === 0) {
    tbody.innerHTML = `<tr><td colspan="9" class="empty-state">Keine Schulen vorhanden</td></tr>`;
    renderTrialAlerts();
    return;
  }
  tbody.innerHTML = schools.map(s => {
    const statusBadge = s.active
      ? `<span class="badge badge-green">Aktiv</span>`
      : `<span class="badge badge-red">Inaktiv</span>`;
    return `<tr data-id="${s.id}" style="${!s.active ? "opacity:.55" : ""}">
      <td>
        <div style="font-weight:600">${esc(s.name)}</div>
        <code class="admin-slug">${esc(s.slug)}</code>
      </td>
      <td class="text-muted">${esc(s.ort || "")}</td>
      <td style="text-align:center">${statusBadge}</td>
      ${MODULES.map(m => moduleCell(s, m)).join("")}
      ${drkCell(s)}
      <td class="text-muted" style="text-align:center">${s.classCount} / ${s.studentCount} / ${s.practiceCount}</td>
      <td style="white-space:nowrap">
        <button class="btn btn-sm btn-secondary btn-users" data-id="${s.id}" title="Benutzer verwalten">Benutzer</button>
        <button class="btn btn-sm btn-secondary btn-toggle" data-id="${s.id}" title="${s.active ? "Deaktivieren" : "Aktivieren"}">${s.active ? Icons.pauseCircle : Icons.playCircle}</button>
        <button class="btn btn-danger btn-del" data-id="${s.id}" title="Löschen">${Icons.trash}</button>
      </td></tr>`;
  }).join("");

  const schoolOf = (btn: Element) => schools.find(s => sameId(s.id, (btn as HTMLElement).dataset.id));

  // Klick auf die Zeile öffnet die Schule, die Schaltflächen darin haben Vorrang.
  tbody.querySelectorAll("tr[data-id]").forEach(tr => {
    tr.addEventListener("click", (e) => {
      const target = e.target as HTMLElement;
      if (target.closest(".btn-toggle") || target.closest(".btn-del") || target.closest(".btn-users")) return;
      const school = schools.find(s => sameId(s.id, tr.getAttribute("data-id")));
      if (school) openSchoolModal(school);
    });
  });

  tbody.querySelectorAll(".btn-users").forEach(btn => btn.addEventListener("click", (e) => {
    e.stopPropagation();
    const school = schoolOf(btn);
    if (school) openSchoolModal(school, "benutzer");
  }));

  tbody.querySelectorAll(".btn-toggle").forEach(btn => btn.addEventListener("click", async (e) => {
    e.stopPropagation();
    const school = schoolOf(btn);
    if (!school) return;
    const activate = !school.active;
    const ok = await confirmDialog({
      title: `Schule ${activate ? "aktivieren" : "deaktivieren"}`,
      message: `„${school.name}" wirklich ${activate ? "aktivieren" : "deaktivieren"}?`,
      confirmLabel: activate ? "Aktivieren" : "Deaktivieren",
      danger: !activate,
    });
    if (!ok) return;
    try {
      await api.admin.schools.toggle(school.id);
      await load();
      renderTable();
      await refreshStats();
      showToast(activate ? "Schule aktiviert" : "Schule deaktiviert");
    } catch (err: any) {
      await infoDialog("Fehler", err.message);
    }
  }));

  tbody.querySelectorAll(".btn-del").forEach(btn => btn.addEventListener("click", async (e) => {
    e.stopPropagation();
    const school = schoolOf(btn);
    if (!school) return;
    const ok = await confirmDialog({
      title: "Schule löschen",
      message: `„${school.name}" wirklich löschen? Das ist nur möglich, solange die Schule keine Daten enthält.`,
      confirmLabel: "Löschen",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.admin.schools.delete(school.id);
      await load();
      renderTable();
      await refreshStats();
      showToast("Schule gelöscht");
    } catch (err: any) {
      // 409 heißt: die Schule enthält noch Daten. Das ist kein Fehler, sondern
      // eine Begründung, die der Admin lesen muss.
      if (err.status === 409) await infoDialog("Löschen nicht möglich", err.message);
      else await infoDialog("Fehler", err.message);
    }
  }));

  renderTrialAlerts();
}

// ── Hinweisleiste: Testphasen ──

interface TrialAlert { school: School; module: ModuleConfig; info: TrialInfo; }

function trialAlerts(): TrialAlert[] {
  const out: TrialAlert[] = [];
  for (const school of schools) {
    for (const module of MODULES) {
      // Dauerhaft freigeschaltete Module überleben jede Testphase, ihr Enddatum
      // ist dann bedeutungslos.
      if (module.manualGrant(school)) continue;
      const info = trialInfo(module.trialEndsAt(school));
      if (!info) continue;
      const relevant = info.expired
        ? info.daysLeft > -TRIAL_EXPIRED_DAYS
        : info.daysLeft <= TRIAL_SOON_DAYS;
      if (relevant) out.push({ school, module, info });
    }
  }
  return out.sort((a, b) => a.info.endsAt.getTime() - b.info.endsAt.getTime());
}

function renderTrialAlerts() {
  const host = page.querySelector("#admin-trials")!;
  const alerts = trialAlerts();
  if (alerts.length === 0) { host.innerHTML = ""; return; }

  host.innerHTML = `<div class="card admin-trial-alert">
    <h2>Testphasen im Blick</h2>
    <ul>${alerts.map((a, i) => `<li>
      <button data-idx="${i}">${esc(a.school.name)}</button>
      <span class="badge ${a.info.expired ? "badge-red" : "badge-amber"}">${esc(a.module.label)}</span>
      <span class="text-muted">${a.info.expired
        ? `abgelaufen am ${formatDate(a.info.endsAt)}`
        : `endet am ${formatDate(a.info.endsAt)}, noch ${dayLabel(a.info.daysLeft)}`}</span>
    </li>`).join("")}</ul>
  </div>`;

  host.querySelectorAll<HTMLButtonElement>("button[data-idx]").forEach(btn => {
    btn.addEventListener("click", () => {
      const alert = alerts[+btn.dataset.idx!];
      openSchoolModal(alert.school, "features");
    });
  });
}

// ── Schul-Fenster ──

type TabId = "stammdaten" | "features" | "fachbereiche" | "benutzer";

const SPINNER = `<div style="display:flex;align-items:center;justify-content:center;height:160px"><div class="spinner"></div></div>`;

const ROLE_LABELS: Record<string, string> = {
  "schul-admin": "Schul-Admin",
  "koordinator": "Koordinator (bearbeiten)",
  "viewer": "Betrachter (nur lesen)",
};

/**
 * Ein einziges Fenster für die Schule. Es bleibt offen, bis der Admin es
 * bewusst verlässt: Freischaltungen, Testphasen, Fachbereiche und Benutzer
 * wirken sofort und zeichnen nur ihren Reiter neu.
 *
 * Beim Anlegen gibt es zuerst nur Stammdaten. Die übrigen Reiter hängen an
 * einer gespeicherten Schul-ID und kommen dazu, sobald sie feststeht, ohne dass
 * das Fenster schließt.
 */
function openSchoolModal(existing?: School, startTab: TabId = "stammdaten") {
  let school = existing;
  let activeTab: TabId = school ? startTab : "stammdaten";
  /** Meldet ungespeicherte Eingaben im gerade gezeigten Formular. */
  let isDirty: () => boolean = () => false;

  openModal(school ? school.name : "Neue Schule anlegen", `
    <div class="detail-tabs" id="s-tabs"></div>
    <div class="alert alert-error hidden" id="s-error"></div>
    <div class="tab-content-wrap" id="s-content"></div>
    <div class="form-actions" id="s-actions"></div>`, { wide: true, fixedSize: true });

  const mb = modalBody();
  const tabsEl = mb.querySelector<HTMLElement>("#s-tabs")!;
  const errorEl = mb.querySelector<HTMLElement>("#s-error")!;
  const content = mb.querySelector<HTMLElement>("#s-content")!;
  const actionsEl = mb.querySelector<HTMLElement>("#s-actions")!;

  /** Meldung bleibt im Fenster stehen, wo die Aktion ausgelöst wurde. Ein Toast
   *  am Bildschirmrand würde daneben leicht übersehen. */
  const showError = (message: string) => {
    errorEl.textContent = message;
    errorEl.classList.remove("hidden");
  };
  const hideError = () => errorEl.classList.add("hidden");

  async function mayLeave(): Promise<boolean> {
    if (!isDirty()) return true;
    return confirmDialog({
      title: "Änderungen verwerfen?",
      message: "In diesem Formular stehen ungespeicherte Eingaben. Beim Verlassen gehen sie verloren.",
      confirmLabel: "Verwerfen",
      danger: true,
    });
  }

  // X, Escape und Klick auf den Hintergrund fragen nach, solange etwas
  // ungespeichert ist. Speichern und Abbrechen schließen ohne Rückfrage.
  setCloseGuard(mayLeave);

  /** Schule und Tabelle aus der Liste auffrischen. Die Einzel-Endpunkte liefern
   *  je nach Aktion nur einen Teil der Felder zurück, das UPDATE etwa ohne die
   *  Zählwerte. Die Liste ist deshalb die verlässliche Quelle. */
  async function refreshSchool(id: number) {
    await load();
    renderTable();
    const found = schools.find(s => sameId(s.id, id));
    if (found) {
      school = found;
      setModalTitle(found.name);
    }
  }

  function renderTabs() {
    // Ohne gespeicherte Schule gibt es nur Stammdaten: alles andere braucht eine ID.
    const tabs: { id: TabId; label: string }[] = [{ id: "stammdaten", label: "Stammdaten" }];
    if (school) {
      tabs.push({ id: "features", label: "Features" });
      tabs.push({ id: "fachbereiche", label: "Fachbereiche" });
      tabs.push({ id: "benutzer", label: "Benutzer" });
    }
    tabsEl.innerHTML = tabs
      .map(t => `<button class="detail-tab${t.id === activeTab ? " active" : ""}" data-tab="${t.id}">${t.label}</button>`)
      .join("");
    tabsEl.querySelectorAll<HTMLButtonElement>(".detail-tab").forEach(btn => {
      btn.addEventListener("click", async () => {
        const target = btn.dataset.tab as TabId;
        if (target === activeTab) return;
        if (!await mayLeave()) return;
        activeTab = target;
        await showTab();
      });
    });
  }

  /** Fußzeile des Fensters. Nur Speichern und Abbrechen verlassen es. */
  function renderActions() {
    if (activeTab === "stammdaten") {
      actionsEl.innerHTML = `
        <button class="btn btn-secondary" id="s-cancel">Abbrechen</button>
        <button class="btn btn-primary" id="s-save">${school ? "Speichern" : "Schule anlegen"}</button>`;
      actionsEl.querySelector("#s-cancel")!.addEventListener("click", closeModal);
      actionsEl.querySelector("#s-save")!.addEventListener("click", saveStammdaten);
    } else {
      actionsEl.innerHTML = `<button class="btn btn-secondary" id="s-close">Schließen</button>`;
      actionsEl.querySelector("#s-close")!.addEventListener("click", closeModal);
    }
  }

  async function showTab() {
    hideError();
    isDirty = () => false;
    renderTabs();
    renderActions();
    if (activeTab === "stammdaten") { renderStammdaten(); return; }
    content.innerHTML = SPINNER;
    try {
      if (activeTab === "features") renderFeatures();
      else if (activeTab === "fachbereiche") await renderFachbereiche();
      else await renderUserList();
    } catch (err: any) {
      content.innerHTML = `<div class="empty-state">Laden fehlgeschlagen: ${esc(err.message)}</div>`;
    }
  }

  /**
   * Aktion ausführen, ohne das Fenster zu verlassen: Schaltfläche sperren, danach
   * Schule und Tabelle auffrischen und den Reiter mit dem neuen Stand zeichnen.
   * Bei einem Fehler bleibt alles stehen und die Schaltfläche wird wieder frei.
   */
  async function apply(btn: HTMLButtonElement | null, action: () => Promise<unknown>, success: string) {
    hideError();
    const label = btn?.textContent;
    if (btn) { btn.disabled = true; btn.textContent = "Bitte warten"; }
    try {
      await action();
      await refreshSchool(school!.id);
      await showTab();
      showToast(success);
    } catch (err: any) {
      showError(err.message);
      if (btn) { btn.disabled = false; if (label !== undefined) btn.textContent = label; }
    }
  }

  // ── Reiter: Stammdaten ──

  function stammdatenValues() {
    const val = (id: string) => (content.querySelector(`#${id}`) as HTMLInputElement | null)?.value ?? "";
    return {
      name: val("f-name").trim(),
      address: val("f-address"),
      plz: val("f-plz"),
      ort: val("f-ort"),
      phone: val("f-phone"),
      email: val("f-email"),
      slug: val("f-slug").trim(),
      adminEmail: val("f-admin-email").trim(),
    };
  }

  function renderStammdaten() {
    const isEdit = !!school;
    const slugHint = isEdit
      ? "Der Slug kann nach dem Anlegen nicht mehr geändert werden."
      : "Nur Kleinbuchstaben, Zahlen und Bindestriche. Wird zur Keycloak-Gruppe schulen/&lt;slug&gt;.";

    content.innerHTML = `
      <div class="form-grid">
        <div class="form-group col-span-2"><label>Name der Schule *</label><input id="f-name" value="${esc(school?.name || "")}" placeholder="z.B. PT-Schule München" /></div>
        <div class="form-group col-span-2"><label>Slug (URL-Kennung) *</label><input id="f-slug" value="${esc(school?.slug || "")}" placeholder="z.B. pt-muenchen" ${isEdit ? "disabled" : ""} /><div class="admin-module-hint">${slugHint}</div></div>
        <div class="form-group col-span-2"><label>Adresse</label><input id="f-address" value="${esc(school?.address || "")}" placeholder="Straße Hausnummer" /></div>
        <div class="form-group"><label>PLZ</label><input id="f-plz" value="${esc(school?.plz || "")}" /></div>
        <div class="form-group"><label>Ort</label><input id="f-ort" value="${esc(school?.ort || "")}" /></div>
        <div class="form-group"><label>Telefon</label><input id="f-phone" value="${esc(school?.phone || "")}" /></div>
        <div class="form-group"><label>E-Mail</label><input id="f-email" value="${esc(school?.email || "")}" /></div>
        ${isEdit ? "" : `<div class="form-group col-span-2"><label>Admin-E-Mail *</label><input id="f-admin-email" type="email" placeholder="admin@schule.de" /><div class="admin-module-hint">Login-Adresse des initialen Schul-Admins. Sie dient zugleich als Benutzername.</div></div>`}
      </div>
      ${isEdit ? "" : `<div class="alert" style="background:var(--primary-50);color:var(--primary-dark);margin-top:1rem">
        <strong>Hinweis:</strong> Die Keycloak-Gruppe <code>/schulen/&lt;slug&gt;</code> und ein initialer Schul-Admin mit der angegebenen Admin-E-Mail werden automatisch angelegt.
      </div>`}`;

    if (!isEdit) {
      // Der Slug folgt dem Namen, bis der Admin ihn selbst anfasst.
      const nameInput = content.querySelector("#f-name") as HTMLInputElement;
      const slugInput = content.querySelector("#f-slug") as HTMLInputElement;
      nameInput.addEventListener("input", () => {
        if (slugInput.dataset.manual) return;
        slugInput.value = nameInput.value.trim().toLowerCase()
          .replace(/[äÄ]/g, "ae").replace(/[öÖ]/g, "oe").replace(/[üÜ]/g, "ue").replace(/[ß]/g, "ss")
          .replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
      });
      slugInput.addEventListener("input", () => { slugInput.dataset.manual = "1"; });
    }

    const snapshot = JSON.stringify(stammdatenValues());
    isDirty = () => JSON.stringify(stammdatenValues()) !== snapshot;
    (content.querySelector("#f-name") as HTMLInputElement).focus();
  }

  async function saveStammdaten() {
    hideError();
    const values = stammdatenValues();
    if (!values.name) { showError("Name ist ein Pflichtfeld"); return; }
    if (!school && !values.slug) { showError("Slug ist ein Pflichtfeld"); return; }
    if (!school && !values.adminEmail) { showError("Admin-E-Mail ist ein Pflichtfeld"); return; }

    const btn = actionsEl.querySelector("#s-save") as HTMLButtonElement;
    btn.disabled = true;
    btn.textContent = "Speichern…";
    const data: Record<string, string> = {
      name: values.name, address: values.address, plz: values.plz,
      ort: values.ort, phone: values.phone, email: values.email,
    };
    try {
      if (school) {
        await api.admin.schools.update(school.id, data);
        await refreshSchool(school.id);
        renderStammdaten();
        renderActions();
        showToast("Schule gespeichert");
      } else {
        data.slug = values.slug;
        data.adminEmail = values.adminEmail;
        const created = await api.admin.schools.create(data) as School & { initialAdmin?: { email: string; password: string } };
        school = created;
        await refreshSchool(created.id);
        await refreshStats();
        // Das Fenster bleibt offen. Features, Fachbereiche und Benutzer kommen
        // jetzt dazu, weil die Schul-ID erst hier feststeht.
        await showTab();
        showToast("Schule angelegt");
        if (created.initialAdmin) showInitialAdminCredentials(created.initialAdmin);
      }
    } catch (err: any) {
      showError(err.message);
      btn.disabled = false;
      btn.textContent = school ? "Speichern" : "Schule anlegen";
    }
  }

  // ── Reiter: Features ──

  function moduleBlockHtml(s: School, m: ModuleConfig): string {
    const granted = m.manualGrant(s);
    const active = m.enabled(s);
    const trial = trialInfo(m.trialEndsAt(s));
    const running = !!trial && !trial.expired;

    const hint = granted ? GRANTED_HINT : active ? ENTITLED_HINT : m.offHint(s);
    const trialState = !trial
      ? `<span class="text-muted">Nicht gesetzt</span>`
      : running
        ? `<span style="color:var(--gray-700)">Aktiv, endet am ${formatDate(trial.endsAt)} (noch ${dayLabel(trial.daysLeft)})</span>`
        : `<span style="color:var(--red-700)">Abgelaufen am ${formatDate(trial.endsAt)}</span>`;
    // Bei laufender Testphase steht ihr eigenes Ende im Feld, sonst zwei Wochen
    // ab heute: ein abgelaufenes Datum als Vorschlag hilft niemandem.
    const dateValue = running ? dateInputValue(trial!.endsAt) : dateInputValue(new Date(Date.now() + 14 * DAY));

    return `<div class="admin-module" data-module="${m.key}">
      <div class="admin-module-head">
        <span class="admin-module-name">${esc(m.label)}</span>
        <span class="badge ${active ? m.badgeClass : "badge-gray"}">${active ? "Aktiv" : "Inaktiv"}</span>
        ${granted ? `<span class="badge badge-amber">Dauerhaft freigeschaltet</span>` : ""}
        <button class="btn btn-sm ${granted ? "btn-secondary" : "btn-primary"}" data-act="toggle">${granted ? "Freischaltung entziehen" : "Dauerhaft freischalten"}</button>
      </div>
      <div class="admin-module-hint">${esc(hint)}</div>
      <div class="admin-trial-row">
        <span class="admin-trial-state"><strong>Testphase:</strong> ${trialState}</span>
        <label>Neues Ende</label>
        <input type="date" data-role="trial-date" value="${dateValue}" />
        <button class="btn btn-sm btn-primary" data-act="set-trial">${running ? "Verlängern" : "Trial starten"}</button>
        ${trial ? `<button class="btn btn-sm btn-secondary" data-act="clear-trial" style="color:var(--red-700)">Entfernen</button>` : ""}
      </div>
    </div>`;
  }

  function wireModuleBlock(s: School, m: ModuleConfig) {
    const block = content.querySelector<HTMLElement>(`[data-module="${m.key}"]`)!;
    const btn = (act: string) => block.querySelector<HTMLButtonElement>(`[data-act="${act}"]`);

    btn("toggle")!.addEventListener("click", async (e) => {
      const target = e.currentTarget as HTMLButtonElement;
      const grant = !m.manualGrant(s);
      const ok = await confirmDialog({
        title: `${m.label} ${grant ? "freischalten" : "entziehen"}`,
        message: `${m.label} für „${s.name}" wirklich ${grant ? "dauerhaft freischalten" : "entziehen"}?\n\n${grant ? m.grantEffect(s) : m.revokeEffect(s)}`,
        confirmLabel: grant ? "Freischalten" : "Entziehen",
        danger: !grant,
      });
      if (!ok) return;
      await apply(target, () => m.toggle(s.id, grant),
        `${m.label} ${grant ? "freigeschaltet" : "entzogen"}`);
    });

    btn("set-trial")!.addEventListener("click", async (e) => {
      const target = e.currentTarget as HTMLButtonElement;
      const value = block.querySelector<HTMLInputElement>('[data-role="trial-date"]')!.value;
      if (!value) { showError("Bitte ein gültiges Datum für das Ende der Testphase angeben."); return; }
      await apply(target, () => m.setTrial(s.id, new Date(value).toISOString()),
        `Testphase ${m.label} gesetzt`);
    });

    btn("clear-trial")?.addEventListener("click", async (e) => {
      const target = e.currentTarget as HTMLButtonElement;
      const ok = await confirmDialog({
        title: `Testphase ${m.label} entfernen`,
        message: `Testphase für „${s.name}" wirklich entfernen? ${m.label} wird deaktiviert, sofern kein Abo läuft und das Modul nicht dauerhaft freigeschaltet ist.`,
        confirmLabel: "Entfernen",
        danger: true,
      });
      if (!ok) return;
      await apply(target, () => m.setTrial(s.id, null), `Testphase ${m.label} entfernt`);
    });
  }

  function renderFeatures() {
    const s = school!;
    content.innerHTML = `
      <fieldset><legend>Keycloak-Zuordnung</legend>
        <div style="font-size:.875rem;color:var(--gray-600)">
          <strong>Keycloak-Gruppe:</strong> <code>${esc(s.keycloakGroup)}</code><br>
          <strong>Bestand:</strong> ${s.classCount} Klassen, ${s.studentCount} Schüler, ${s.practiceCount} Praktikumsstellen
        </div>
      </fieldset>
      <fieldset style="margin-top:1rem"><legend>Module</legend>
        ${MODULES.map(m => moduleBlockHtml(s, m)).join("")}
        <div class="admin-module" data-module="drk">
          <div class="admin-module-head">
            <span class="admin-module-name">DRK Schule mit Kreisverbänden</span>
            <span class="badge ${s.drkKreisverbandEnabled ? "badge-red" : "badge-gray"}">${s.drkKreisverbandEnabled ? "Aktiv" : "Inaktiv"}</span>
            <button class="btn btn-sm ${s.drkKreisverbandEnabled ? "btn-secondary" : "btn-primary"}" data-act="toggle-drk">${s.drkKreisverbandEnabled ? "Deaktivieren" : "Freischalten"}</button>
          </div>
          <div class="admin-module-hint">${s.drkKreisverbandEnabled
            ? "Die Schule kann eigene DRK-Kreisverbände pflegen, Schüler einem Kreisverband zuordnen (auch per CSV-Import) und in der Planung nach Kreisverband exportieren."
            : "Freischalten blendet den Menüpunkt „Kreisverbände\", das Kreisverband-Feld am Schüler und die Export-Option in der Planung ein."}</div>
        </div>
      </fieldset>`;

    for (const m of MODULES) wireModuleBlock(s, m);

    // DRK ist ein reines Datenbank-Flag: keine Testphase, kein Keycloak.
    content.querySelector<HTMLButtonElement>('[data-act="toggle-drk"]')!.addEventListener("click", async (e) => {
      const target = e.currentTarget as HTMLButtonElement;
      const enable = !s.drkKreisverbandEnabled;
      const ok = await confirmDialog({
        title: `DRK-Kreisverbände ${enable ? "freischalten" : "deaktivieren"}`,
        message: `DRK Schule mit Kreisverbänden für „${s.name}" wirklich ${enable ? "freischalten" : "deaktivieren"}?`,
        confirmLabel: enable ? "Freischalten" : "Deaktivieren",
        danger: !enable,
      });
      if (!ok) return;
      await apply(target, () => api.admin.schools.toggleDrk(s.id, enable),
        enable ? "DRK-Kreisverbände freigeschaltet" : "DRK-Kreisverbände deaktiviert");
    });
  }

  // ── Reiter: Fachbereiche ──

  async function renderFachbereiche() {
    const s = school!;
    const { departmentGroupName, departments, standardGroups, customGroups } =
      await api.admin.schools.departments.list(s.id);

    // Ein Fachbereich ohne eigene Zuordnung gehört über den globalen Katalog zu
    // seinen Standardgruppen; das ist der leere Wert.
    const groupOptions = (d: AdminSchoolDepartment): string => {
      const current = d.customGroupId ? `c-${d.customGroupId}` : d.extendsGroupId ? `p-${d.extendsGroupId}` : "";
      const catalogNames = d.catalogGroupIds
        .map(id => standardGroups.find(g => sameId(g.id, id))?.name)
        .filter((n): n is string => !!n);
      const noneLabel = catalogNames.length > 0 ? `Standard-Katalog (${catalogNames.join(", ")})` : "Keine Gruppe";
      const opts = [`<option value=""${current === "" ? " selected" : ""}>${esc(noneLabel)}</option>`];
      for (const g of standardGroups) {
        opts.push(`<option value="p-${g.id}"${current === `p-${g.id}` ? " selected" : ""}>${esc(g.name)}</option>`);
      }
      for (const g of customGroups) {
        opts.push(`<option value="c-${g.id}"${current === `c-${g.id}` ? " selected" : ""}>Eigene: ${esc(g.name)}</option>`);
      }
      return opts.join("");
    };

    const deptRows = departments.length === 0
      ? `<tr><td colspan="6" class="empty-state">Keine Fachbereiche konfiguriert</td></tr>`
      : departments.map(d => `
        <tr>
          <td><span style="display:inline-block;width:.7rem;height:.7rem;border-radius:50%;background:${esc(d.color)};margin-right:.5rem;vertical-align:middle"></span>${esc(d.nameDe)}</td>
          <td class="text-muted">${esc(d.shortName)}</td>
          <td class="text-muted" style="text-align:center">${d.requiredHours}</td>
          <td><select class="fb-group-select" data-id="${esc(d.id)}" style="max-width:220px">${groupOptions(d)}</select></td>
          <td style="text-align:center">${d.active !== false ? `<span class="badge badge-green">Aktiv</span>` : `<span class="badge badge-gray">Inaktiv</span>`}</td>
          <td style="text-align:right"><button class="btn btn-sm btn-danger fb-del-dept" data-id="${esc(d.id)}" title="Fachbereich löschen">${Icons.trash}</button></td>
        </tr>`).join("");

    const groupRows = customGroups.length === 0
      ? `<tr><td colspan="2" class="empty-state">Keine eigenen Gruppen</td></tr>`
      : customGroups.map(g => `
        <tr>
          <td>${esc(g.name)}</td>
          <td style="text-align:right"><button class="btn btn-sm btn-danger fb-del-group" data-id="${g.id}" title="Gruppe löschen">${Icons.trash}</button></td>
        </tr>`).join("");

    content.innerHTML = `
      <div style="font-size:.875rem;color:var(--gray-600);margin-bottom:1rem">
        Verknüpfte Fachbereichsgruppe: <strong>${departmentGroupName ? esc(departmentGroupName) : "Keine"}</strong>
      </div>
      <h3 style="font-size:.9rem;font-weight:600;margin-bottom:.5rem">Fachbereiche</h3>
      <div class="admin-module-hint" style="margin-bottom:.5rem">Eine geänderte Gruppe wirkt nur für diese Schule. Kapazitäten und Zuweisungen bleiben dabei erhalten.</div>
      <div class="table-wrap"><table><thead><tr>
        <th>Name</th><th>Kürzel</th><th style="text-align:center">Pflichtstunden</th><th>Gruppe</th><th style="text-align:center">Status</th><th></th>
      </tr></thead><tbody>${deptRows}</tbody></table></div>
      <h3 style="font-size:.9rem;font-weight:600;margin:1.25rem 0 .5rem">Eigene Fachbereichsgruppen</h3>
      <div class="admin-module-hint" style="margin-bottom:.5rem">Das Löschen einer Gruppe entkoppelt nur ihre Fachbereiche und Klassen. Diese werden dabei nicht gelöscht.</div>
      <div class="table-wrap"><table><tbody>${groupRows}</tbody></table></div>`;

    content.querySelectorAll<HTMLSelectElement>(".fb-group-select").forEach(sel => {
      sel.addEventListener("change", async () => {
        const raw = sel.value;
        const kind = raw.startsWith("c-") ? "custom" : raw.startsWith("p-") ? "standard" : "none";
        sel.disabled = true;
        try {
          await api.admin.schools.departments.setGroup(s.id, sel.dataset.id!, kind, raw ? +raw.slice(2) : undefined);
          showToast("Gruppe geändert");
        } catch (err: any) {
          showError(err.message);
        }
        await renderFachbereiche();
      });
    });

    content.querySelectorAll<HTMLButtonElement>(".fb-del-dept").forEach(btn => {
      btn.addEventListener("click", async () => {
        const dept = departments.find(d => sameId(d.id, btn.dataset.id));
        const ok = await confirmDialog({
          title: "Fachbereich löschen",
          message: `Fachbereich „${dept?.nameDe}" wirklich löschen?`,
          confirmLabel: "Löschen",
          danger: true,
        });
        if (!ok) return;
        btn.disabled = true;
        try {
          await api.admin.schools.departments.delete(s.id, btn.dataset.id!);
          showToast("Fachbereich gelöscht");
          await renderFachbereiche();
        } catch (err: any) {
          showError(err.message);
          btn.disabled = false;
        }
      });
    });

    content.querySelectorAll<HTMLButtonElement>(".fb-del-group").forEach(btn => {
      btn.addEventListener("click", async () => {
        const id = +btn.dataset.id!;
        const group = customGroups.find(g => sameId(g.id, btn.dataset.id));
        const ok = await confirmDialog({
          title: "Gruppe löschen",
          message: `Gruppe „${group?.name}" wirklich löschen?`,
          confirmLabel: "Löschen",
          danger: true,
        });
        if (!ok) return;
        btn.disabled = true;
        try {
          await api.admin.schools.customGroups.delete(s.id, id);
          showToast("Gruppe gelöscht");
          await renderFachbereiche();
        } catch (err: any) {
          showError(err.message);
          btn.disabled = false;
        }
      });
    });
  }

  // ── Reiter: Benutzer ──
  // Liste, Formular und Passwort teilen sich den Reiter. Sie ersetzen einander im
  // Inhalt, statt jeweils ein eigenes Fenster zu öffnen.

  async function renderUserList() {
    const s = school!;
    isDirty = () => false;
    const users = await api.admin.schools.users.list(s.id);

    const rows = users.length === 0
      ? `<tr><td colspan="5" class="empty-state">Keine Benutzer vorhanden</td></tr>`
      : users.map(u => `
        <tr>
          <td style="font-weight:500">${esc(`${u.firstName} ${u.lastName}`.trim())}</td>
          <td class="text-muted">${esc(u.email)}</td>
          <td><span class="badge ${u.role === "schul-admin" ? "badge-blue" : "badge-gray"}">${esc(ROLE_LABELS[u.role] || u.role)}</span></td>
          <td>${u.enabled ? `<span class="badge badge-green">Aktiv</span>` : `<span class="badge badge-red">Deaktiviert</span>`}</td>
          <td style="white-space:nowrap">
            <button class="btn btn-sm btn-secondary au-edit" data-uid="${u.id}">Bearbeiten</button>
            <button class="btn btn-sm btn-secondary au-toggle" data-uid="${u.id}">${u.enabled ? "Deaktivieren" : "Aktivieren"}</button>
            <button class="btn btn-sm btn-secondary au-pw" data-uid="${u.id}">Passwort</button>
            <button class="btn btn-sm btn-danger au-del" data-uid="${u.id}" title="Benutzer löschen">${Icons.trash}</button>
          </td>
        </tr>`).join("");

    content.innerHTML = `
      <div style="margin-bottom:.75rem"><button class="btn btn-primary btn-sm" id="au-add">+ Neuer Benutzer</button></div>
      <div class="table-wrap"><table><thead><tr>
        <th>Name</th><th>E-Mail</th><th>Rolle</th><th>Status</th><th style="width:300px">Aktionen</th>
      </tr></thead><tbody>${rows}</tbody></table></div>`;

    const userOf = (btn: Element) => users.find(u => sameId(u.id, (btn as HTMLElement).dataset.uid));

    content.querySelector("#au-add")!.addEventListener("click", () => renderUserForm());
    content.querySelectorAll(".au-edit").forEach(btn => btn.addEventListener("click", () => {
      const u = userOf(btn);
      if (u) renderUserForm(u);
    }));
    content.querySelectorAll(".au-pw").forEach(btn => btn.addEventListener("click", () => {
      const u = userOf(btn);
      if (u) renderUserPassword(u);
    }));

    content.querySelectorAll<HTMLButtonElement>(".au-toggle").forEach(btn => btn.addEventListener("click", async () => {
      const u = userOf(btn);
      if (!u) return;
      hideError();
      btn.disabled = true;
      try {
        await api.admin.schools.users.toggle(s.id, u.id, !u.enabled);
        showToast(u.enabled ? "Benutzer deaktiviert" : "Benutzer aktiviert");
        await renderUserList();
      } catch (err: any) {
        showError(err.message);
        btn.disabled = false;
      }
    }));

    content.querySelectorAll<HTMLButtonElement>(".au-del").forEach(btn => btn.addEventListener("click", async () => {
      const u = userOf(btn);
      if (!u) return;
      const ok = await confirmDialog({
        title: "Benutzer löschen",
        message: `Benutzer „${u.email}" wirklich löschen?`,
        confirmLabel: "Löschen",
        danger: true,
      });
      if (!ok) return;
      hideError();
      btn.disabled = true;
      try {
        await api.admin.schools.users.delete(s.id, u.id);
        showToast("Benutzer gelöscht");
        await renderUserList();
      } catch (err: any) {
        showError(err.message);
        btn.disabled = false;
      }
    }));
  }

  function renderUserForm(u?: SchoolUser) {
    const s = school!;
    hideError();
    const isEdit = !!u;
    const roleOpts = Object.entries(ROLE_LABELS)
      .map(([val, label]) => `<option value="${val}" ${u?.role === val ? "selected" : ""}>${label}</option>`)
      .join("");

    content.innerHTML = `
      <h3 style="font-size:.9rem;font-weight:600;margin-bottom:.75rem">${isEdit ? `Benutzer bearbeiten` : "Neuer Benutzer"}</h3>
      <div class="form-grid">
        <div class="form-group col-span-2"><label>E-Mail *</label><input id="fu-email" type="email" value="${esc(u?.email || "")}" /></div>
        <div class="form-group"><label>Vorname</label><input id="fu-fn" value="${esc(u?.firstName || "")}" /></div>
        <div class="form-group"><label>Nachname</label><input id="fu-ln" value="${esc(u?.lastName || "")}" /></div>
        <div class="form-group col-span-2"><label>Rolle</label><select id="fu-role">${roleOpts}</select></div>
        ${isEdit ? "" : `<div class="form-group col-span-2"><label>Temporäres Passwort *</label><input id="fu-pw" type="text" placeholder="Wird beim ersten Login geändert" /><ul id="fu-pw-checklist" class="pw-checklist"></ul></div>`}
      </div>
      <div class="form-actions"><button class="btn btn-secondary" id="fu-back">Abbrechen</button><button class="btn btn-primary" id="fu-save">Speichern</button></div>`;

    const val = (id: string) => (content.querySelector(`#${id}`) as HTMLInputElement | HTMLSelectElement).value;
    const values = () => ({ email: val("fu-email").trim(), firstName: val("fu-fn").trim(), lastName: val("fu-ln").trim(), role: val("fu-role") });
    const snapshot = JSON.stringify(values());
    // Das Passwort steht bewusst nicht im Snapshot, es wird nie vorbelegt. Eine
    // Eingabe darin ist deshalb für sich schon eine ungespeicherte Änderung.
    isDirty = () => JSON.stringify(values()) !== snapshot || (!isEdit && val("fu-pw").length > 0);

    if (!isEdit) {
      mountPasswordChecklist(
        content.querySelector<HTMLInputElement>("#fu-pw")!,
        content.querySelector<HTMLElement>("#fu-pw-checklist")!,
      );
    }
    content.querySelector("#fu-back")!.addEventListener("click", () => { void renderUserList(); });

    const saveBtn = content.querySelector("#fu-save") as HTMLButtonElement;
    saveBtn.addEventListener("click", async () => {
      hideError();
      const data = values();
      if (!data.email) { showError("E-Mail ist ein Pflichtfeld"); return; }
      let password = "";
      if (!isEdit) {
        password = val("fu-pw");
        if (!password) { showError("Temporäres Passwort ist ein Pflichtfeld"); return; }
        const pwError = validatePassword(password);
        if (pwError) { showError(pwError); return; }
      }
      saveBtn.disabled = true;
      saveBtn.textContent = "Speichern…";
      try {
        if (isEdit) await api.admin.schools.users.update(s.id, u!.id, data);
        else await api.admin.schools.users.create(s.id, { ...data, password });
        isDirty = () => false;
        showToast(isEdit ? "Benutzer gespeichert" : "Benutzer angelegt");
        await renderUserList();
      } catch (err: any) {
        showError(err.message);
        saveBtn.disabled = false;
        saveBtn.textContent = "Speichern";
      }
    });
  }

  function renderUserPassword(u: SchoolUser) {
    const s = school!;
    hideError();
    content.innerHTML = `
      <h3 style="font-size:.9rem;font-weight:600;margin-bottom:.75rem">Passwort zurücksetzen</h3>
      <p style="font-size:.875rem">Neues temporäres Passwort für <strong>${esc(u.email)}</strong> setzen.</p>
      <div class="admin-module-hint" style="margin-bottom:.75rem">Der Benutzer muss das Passwort beim nächsten Login ändern.</div>
      <div class="form-group"><label>Neues Passwort *</label><input id="fu-pw" type="text" /><ul id="fu-pw-checklist" class="pw-checklist"></ul></div>
      <div class="form-actions"><button class="btn btn-secondary" id="fu-back">Abbrechen</button><button class="btn btn-primary" id="fu-save">Passwort setzen</button></div>`;

    const input = content.querySelector("#fu-pw") as HTMLInputElement;
    mountPasswordChecklist(input, content.querySelector<HTMLElement>("#fu-pw-checklist")!);
    isDirty = () => input.value.length > 0;
    content.querySelector("#fu-back")!.addEventListener("click", () => { void renderUserList(); });

    const saveBtn = content.querySelector("#fu-save") as HTMLButtonElement;
    saveBtn.addEventListener("click", async () => {
      hideError();
      const password = input.value;
      if (!password) { showError("Passwort ist ein Pflichtfeld"); return; }
      const pwError = validatePassword(password);
      if (pwError) { showError(pwError); return; }
      saveBtn.disabled = true;
      saveBtn.textContent = "Bitte warten";
      try {
        await api.admin.schools.users.resetPassword(s.id, u.id, password);
        isDirty = () => false;
        showToast("Passwort gesetzt");
        await renderUserList();
      } catch (err: any) {
        showError(err.message);
        saveBtn.disabled = false;
        saveBtn.textContent = "Passwort setzen";
      }
    });
  }

  void showTab();
}

/**
 * Zugangsdaten des initialen Admins. Läuft auf der zweiten Modal-Ebene über dem
 * Schul-Fenster: das bleibt damit offen, und die gerade angelegte Schule muss
 * nicht erneut gesucht werden, um Module freizuschalten.
 */
function showInitialAdminCredentials(admin: { email: string; password: string }) {
  openModal2("Initialer Admin-Zugang erstellt", `
    <div class="alert" style="background:var(--amber-50, #fffbeb);color:var(--amber-800, #92400e);margin-bottom:1rem;padding:.75rem 1rem;border-radius:.5rem;border:1px solid var(--amber-200, #fde68a)">
      <strong>Wichtig:</strong> Dieses Passwort wird nur einmalig angezeigt und kann danach nicht mehr abgerufen werden. Bitte notiere es jetzt.
    </div>
    <div class="form-grid">
      <div class="form-group col-span-2">
        <label>E-Mail (Login)</label>
        <input id="ia-user" value="${esc(admin.email)}" readonly style="background:var(--gray-50);font-family:monospace;font-weight:600" />
      </div>
      <div class="form-group col-span-2">
        <label>Passwort</label>
        <div style="display:flex;gap:.5rem">
          <input id="ia-pass" type="password" value="${esc(admin.password)}" readonly
            style="flex:1;background:var(--gray-50);font-family:monospace;font-weight:600" />
          <button id="ia-pass-toggle" class="btn btn-secondary" style="white-space:nowrap">Anzeigen</button>
        </div>
      </div>
    </div>
    <p style="font-size:.85rem;color:var(--gray-500);margin-top:.5rem">Das Passwort muss beim ersten Login geändert werden.</p>
    <div class="form-actions"><button class="btn btn-primary" id="ia-close">Schließen</button></div>`);

  const mb = modalBody2();
  mb.querySelector("#ia-close")!.addEventListener("click", closeModal2);

  // Klick auf das Feld wählt den Text aus, das erleichtert das Kopieren.
  mb.querySelectorAll<HTMLInputElement>("#ia-user, #ia-pass").forEach(input => {
    input.addEventListener("click", () => input.select());
  });

  const toggleBtn = mb.querySelector<HTMLButtonElement>("#ia-pass-toggle")!;
  toggleBtn.addEventListener("click", () => {
    const field = mb.querySelector<HTMLInputElement>("#ia-pass")!;
    const hidden = field.type === "password";
    field.type = hidden ? "text" : "password";
    toggleBtn.textContent = hidden ? "Verbergen" : "Anzeigen";
  });
}

// ── Seite ──

export async function render(el: HTMLElement) {
  page = el;
  el.innerHTML = `
    <div class="page-header">
      <div>
        <h1>Plattform-Administration</h1>
        <div style="font-size:.875rem;color:var(--gray-500);margin-top:.25rem">Mandantenverwaltung: Schulen anlegen, bearbeiten und verwalten</div>
      </div>
      <div style="display:flex;gap:.5rem"><button class="btn btn-secondary" id="btn-csv">CSV Import</button><button class="btn btn-primary" id="btn-add-school">+ Neue Schule</button></div>
    </div>
    <div class="stat-grid" id="admin-stats"></div>
    <div id="admin-trials"></div>
    <div style="margin-top:1.5rem">
      <div class="card table-wrap">
        <table>
          <thead><tr>
            <th>Name</th><th>Ort</th><th style="text-align:center">Status</th>
            ${MODULES.map(m => `<th class="admin-mod">${esc(m.label)}</th>`).join("")}
            <th class="admin-mod">DRK</th>
            <th style="text-align:center">Klassen / Schüler / Stellen</th><th style="width:220px">Aktionen</th>
          </tr></thead>
          <tbody id="school-tbody"></tbody>
        </table>
      </div>
      <div class="admin-legend">
        <span><span class="admin-mod-on">✓</span> dauerhaft aktiv, über Freischaltung oder Abo</span>
        <span><span class="admin-mod-trial">T</span> läuft über eine Testphase</span>
        <span>Leer bedeutet nicht aktiv. Restlaufzeiten stehen oben in der Hinweisleiste.</span>
      </div>
    </div>
    <div class="card" style="margin-top:1.5rem"><div class="card-body">
      <h2 style="font-size:1rem;font-weight:600;margin-bottom:.75rem">Anleitung: Neuen Mandanten einrichten</h2>
      <div style="font-size:.875rem;color:var(--gray-600);line-height:1.7">
        <strong>1.</strong> Hier oben „Neue Schule" anlegen (Name und Slug wählen). Die Keycloak-Gruppe und ein initialer Schul-Admin werden automatisch erstellt.<br>
        <strong>2.</strong> Die Zugangsdaten des initialen Admins werden einmalig angezeigt, bitte sofort notieren.<br>
        <strong>3.</strong> Im selben Fenster unter „Features" die gebuchten Module freischalten oder eine Testphase starten.
      </div>
    </div></div>`;

  el.querySelector("#btn-add-school")!.addEventListener("click", () => openSchoolModal());
  el.querySelector("#btn-csv")!.addEventListener("click", () => {
    openCsvImportModal({
      title: "CSV Import: Schulen",
      duplicateHint: "Bereits vorhandene Schulen (gleicher Name oder Slug) werden übersprungen.",
      fieldDescriptions: [
        { field: "Name",    hint: "Pflichtfeld" },
        { field: "Slug",    hint: "Optional, wird automatisch generiert; nur a bis z, 0 bis 9 und Bindestrich erlaubt" },
        { field: "Adresse", hint: "Optional" },
        { field: "PLZ",     hint: "Optional" },
        { field: "Ort",     hint: "Optional" },
        { field: "Telefon", hint: "Optional" },
        { field: "E-Mail",  hint: "Optional" },
      ],
      onDownloadTemplate: () => api.admin.schools.csvTemplate(),
      onImport: (file) => api.admin.schools.csvImport(file),
      onComplete: async () => { await load(); renderTable(); await refreshStats(); },
    });
  });

  // Daten laden
  const [stats] = await Promise.all([api.admin.stats(), load()]);
  renderStats(stats);
  renderTable();
}
